import { inject, Injectable } from '@angular/core'
import { VisioPopupService } from '@visio-ai/ui-kit'
import { Observable, switchMap } from 'rxjs'
import { ScheduleRuleUpdate } from '../models/schedule-rule.model'
import { ShopsService } from './shops.service'
import { SupabaseService } from './supabase.service'

@Injectable({ providedIn: 'root' })
export class ScheduleRuleActionsService {
  private supabase = inject(SupabaseService)
  private shopsService = inject(ShopsService)
  private visioPopup = inject(VisioPopupService)

  initSession() {
    this.supabase.initSession()
  }

  buildWeekdayUpdate(weekdays: number[]): ScheduleRuleUpdate {
    const updates: ScheduleRuleUpdate = { weekdays }
    if (weekdays.length === 0) updates.active = false
    return updates
  }

  saveSchedulingRequirements(shopId: string, requirements: string) {
    return this.shopsService
      .updateShopSchedulingRequirements(shopId, requirements)
      .pipe(switchMap(() => this.supabase.updateScheduleRulesFromAdmin(shopId)))
  }

  /**
   * Opens the "update rules from admin" confirmation popup and triggers the
   * Supabase update on confirm. Returns an Observable that emits once the
   * update completes, or completes without emitting if the user cancels.
   *
   * @param onConfirm - Optional hook called right after the user confirms,
   *   before the Supabase call. Useful for showing a loading indicator.
   */
  confirmUpdateFromAdmin(
    popupId: string,
    shopId: string | undefined,
    onConfirm?: () => void
  ): Observable<void> {
    const body = shopId
      ? 'Ao confirmar a solicitação, as regras para o estabelecimento selecionado serão atualizadas, essa operação pode levar até um minuto para ser concluída completamente'
      : 'Ao confirmar a solicitação, as regras para todos os estabelecimentos serão atualizadas, essa operação pode levar até um minuto para ser concluída completamente'

    return new Observable(observer => {
      this.visioPopup.callPopup(
        {
          title: 'Atualização de regras',
          body,
          confirm: true,
          id: popupId,
          button: { success: 'Atualizar regras', cancel: 'Cancelar' }
        },
        async confirm => {
          if (!confirm.accept) {
            observer.complete()
            return
          }
          onConfirm?.()
          this.supabase.updateScheduleRulesFromAdmin(shopId).subscribe({
            next: () => { observer.next(); observer.complete() },
            error: err => observer.error(err)
          })
        }
      )
    })
  }
}
